import type { ColumnDef } from '@tanstack/react-table';
import { createColumnHelper } from '@tanstack/react-table';
import { createTablePage } from '@/admin/ui/createTablePage';
import api from '@/api';
import i18n from '~/i18n';
import {
  type AdoptionRow,
  adoptionColumns,
  type GradeStudent,
  gradeColumns,
  type HopefulStudent,
  hopefulColumns,
  LANGUAGES,
  REQUEST_STATUSES,
  type RequestRow,
  requestColumns,
  studentFilterFields,
  type WaitingStudent,
  waitingColumns,
} from './columns';
import {
  field,
  filterInt,
  filterInts,
  filterString,
  filterStrings,
  listQuery,
  WAITING_PAGE_SIZE,
} from './queryArgs';
import {
  HelperActions,
  PassOnAction,
  RejectAction,
  SwitchActions,
} from './RowActions';

const STUDENT_ROUTE = '/admin/students/$studentId' as const;
const studentParams = (id: number) => ({ studentId: String(id) });

function withActions<D>(
  columns: ColumnDef<D, any>[],
  render: (row: D) => React.ReactNode,
): ColumnDef<D, any>[] {
  const helper = createColumnHelper<D>();
  return [
    ...columns,
    helper.display({
      id: 'actions',
      header: () => i18n.t('admin.waiting.col.actions'),
      cell: ({ row }) => render(row.original),
    }),
  ];
}

const studentSort = { id: 'id', name: 'name', last_active: 'last_active' };

export const WaitingTab = createTablePage<WaitingStudent, typeof STUDENT_ROUTE>(
  {
    name: 'admin.waiting.tab.waiting',
    searchable: true,
    pageSize: WAITING_PAGE_SIZE,
    urlState: true,
    searchPrefix: 'waiting_',
    sortKeys: studentSort,
    defaultSort: { key: 'id', order: 'desc' },
    filterFields: () => studentFilterFields(true),
    helperPickerKeys: ['study_helper_id'],
    useData: (_page, search, extras) =>
      api.listAdminStudentsWaiting.useQuery({
        query: {
          ...listQuery(search, extras),
          student_id: filterInt(extras, 'student_id'),
          first_name: filterString(extras, 'first_name'),
          last_name: filterString(extras, 'last_name'),
          study_mode: filterStrings(extras, 'study_mode'),
          language: filterStrings(extras, 'language'),
          study_helper_id: filterInts(extras, 'study_helper_id'),
          waiting_on: filterString(extras, 'waiting_on') as never,
        },
      }),
    columns: withActions(waitingColumns, (r) => (
      <HelperActions
        studentId={r.id}
        helper={r.study_helper}
        studyGroupSlug={r.study_group_slug}
      />
    )),
    itemBaseUrl: STUDENT_ROUTE,
    getParams: (r) => studentParams(r.id),
  },
);

export const GradesTab = createTablePage<GradeStudent, typeof STUDENT_ROUTE>({
  name: 'admin.waiting.tab.grades',
  searchable: true,
  pageSize: WAITING_PAGE_SIZE,
  urlState: true,
  searchPrefix: 'grades_',
  sortKeys: { id: 'id', name: 'name', ungraded_since: 'ungraded_since' },
  defaultSort: { key: 'ungraded_since', order: 'asc' },
  filterFields: () => studentFilterFields(false),
  helperPickerKeys: ['study_helper_id'],
  useData: (_page, search, extras) =>
    api.listAdminStudentsWaitingGrades.useQuery({
      query: {
        ...listQuery(search, extras),
        student_id: filterInt(extras, 'student_id'),
        first_name: filterString(extras, 'first_name'),
        last_name: filterString(extras, 'last_name'),
        study_mode: filterStrings(extras, 'study_mode'),
        language: filterStrings(extras, 'language'),
        study_helper_id: filterInts(extras, 'study_helper_id'),
      },
    }),
  columns: withActions(gradeColumns, (r) => (
    <HelperActions
      studentId={r.id}
      helper={r.study_helper}
      studyGroupSlug={r.study_group_slug}
    />
  )),
  itemBaseUrl: STUDENT_ROUTE,
  getParams: (r) => studentParams(r.id),
});

const requestSort = {
  name: 'name',
  registered: 'registered',
  requested_at: 'requested_at',
};

function requestsTab(kind: 'switch' | 'adoption') {
  return createTablePage<RequestRow, typeof STUDENT_ROUTE>({
    name:
      kind === 'switch'
        ? 'admin.waiting.requests.switch'
        : 'admin.waiting.requests.adoption',
    searchable: true,
    pageSize: WAITING_PAGE_SIZE,
    urlState: true,
    searchPrefix: 'requests_',
    sortKeys: requestSort,
    defaultSort: { key: 'requested_at', order: 'desc' },
    useData: (_page, search, extras) =>
      api.listAdminAdoptionRequests.useQuery({
        query: { ...listQuery(search, extras), kind },
      }),
    columns: withActions(requestColumns(kind), (r) => (
      <>
        <HelperActions
          studentId={r.student.id}
          helper={r.current_helper}
          studyGroupSlug={r.current_helper_group_slug}
        />
        {kind === 'switch' ? (
          <SwitchActions
            requestId={r.id}
            studentName={r.student.display_name}
            studentEmail={r.student_email}
          />
        ) : (
          <RejectAction requestId={r.id} />
        )}
      </>
    )),
    itemBaseUrl: STUDENT_ROUTE,
    getParams: (r) => studentParams(r.student.id),
  });
}

export const SwitchRequestsTab = requestsTab('switch');
export const AdoptionRequestsTab = requestsTab('adoption');

export const HistoryTab = createTablePage<RequestRow, typeof STUDENT_ROUTE>({
  name: 'admin.waiting.requests.history',
  searchable: true,
  pageSize: WAITING_PAGE_SIZE,
  urlState: true,
  searchPrefix: 'requests_',
  sortKeys: requestSort,
  defaultSort: { key: 'requested_at', order: 'desc' },
  helperPickerKeys: ['study_helper_id'],
  filterFields: () => [
    field('student_id', i18n.t('admin.common.id'), 'text'),
    field('study_helper_id', i18n.t('admin.waiting.col.studyHelper'), 'text'),
    field(
      'status',
      i18n.t('admin.common.status'),
      'multiselect',
      REQUEST_STATUSES.map((value) => ({
        value,
        label: i18n.t(`admin.waiting.status.${value}`),
      })),
    ),
  ],
  useData: (_page, search, extras) =>
    api.listAdminAdoptionRequestHistory.useQuery({
      query: {
        ...listQuery(search, extras),
        student_id: filterInt(extras, 'student_id'),
        study_helper_id: filterInts(extras, 'study_helper_id'),
        status: filterStrings(extras, 'status'),
      },
    }),
  columns: requestColumns('history'),
  itemBaseUrl: STUDENT_ROUTE,
  getParams: (r) => studentParams(r.student.id),
});

export const HopefulTab = createTablePage<HopefulStudent, typeof STUDENT_ROUTE>(
  {
    name: 'admin.waiting.tab.hopeful',
    searchable: true,
    pageSize: WAITING_PAGE_SIZE,
    urlState: true,
    searchPrefix: 'hopeful_',
    sortKeys: { id: 'id', name: 'name', registered: 'registered' },
    defaultSort: { key: 'id', order: 'asc' },
    filterFields: () => [
      field(
        'language',
        i18n.t('admin.common.language'),
        'multiselect',
        LANGUAGES.map((value) => ({
          value,
          label: i18n.t(`admin.waiting.lang.${value}`),
        })),
      ),
      field('intro', i18n.t('admin.waiting.col.intro'), 'select', [
        { value: 'started', label: i18n.t('admin.waiting.intro.started') },
        { value: 'completed', label: i18n.t('admin.waiting.intro.completed') },
      ]),
      field(
        'matched_group',
        i18n.t('admin.waiting.col.matchedTargets'),
        'text',
      ),
    ],
    useData: (_page, search, extras) =>
      api.listAdminHopefulStudents.useQuery({
        query: {
          ...listQuery(search, extras),
          language: filterStrings(extras, 'language'),
          intro: filterString(extras, 'intro') as never,
          matched_group: filterString(extras, 'matched_group'),
        },
      }),
    columns: withActions(hopefulColumns, (r) => (
      <PassOnAction studentId={r.id} />
    )),
    itemBaseUrl: STUDENT_ROUTE,
    getParams: (r) => studentParams(r.id),
  },
);

export const AdoptionsTab = createTablePage<AdoptionRow, typeof STUDENT_ROUTE>({
  name: 'admin.waiting.tab.adoptions',
  pageSize: WAITING_PAGE_SIZE,
  urlState: true,
  searchPrefix: 'adoptions_',
  helperPickerKeys: ['study_helper_id'],
  filterFields: () => [
    field('student_id', i18n.t('admin.common.id'), 'text'),
    field('study_helper_id', i18n.t('admin.waiting.col.studyHelper'), 'text'),
    field('adopted_from', i18n.t('admin.waiting.filter.adoptedFrom'), 'date'),
    field('adopted_to', i18n.t('admin.waiting.filter.adoptedTo'), 'date'),
  ],
  useData: (_page, _search, extras) =>
    api.listAdminAdoptions.useQuery({
      query: {
        limit: extras.paging.limit,
        offset: extras.paging.offset,
        student_id: filterInt(extras, 'student_id'),
        study_helper_id: filterInts(extras, 'study_helper_id'),
        adopted_from: filterString(extras, 'adopted_from'),
        adopted_to: filterString(extras, 'adopted_to'),
      },
    }),
  columns: adoptionColumns,
  itemBaseUrl: STUDENT_ROUTE,
  getParams: (r) => studentParams(r.student.id),
});
